import axios from "axios";
import Icon from "components/Icons/Icon";
import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { AI_TRAVEL_PLANNER_CONTENT } from "components/website/marketing/content/featurePageContent";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import React, { useState } from "react";
import { OsTypes, isMobile, osName } from "react-device-detect";
import useReactRouter from "use-react-router";
import FAQSection from "../components/FAQSection";
import DownloadAppModal from "../components/DownloadAppModal";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import Footer from "../components/Footer";
import HowItWorksSection from "../components/HowItWorksSection";
import Navigation from "../components/navigation/Navigation";
import LanguageSwitcher from "../components/LanguageSwitcher";
import "../css/AiTravelPlanner.scss";
import "../css/Website.scss";
import AiVisual1 from "../img/screenshots/ai/AI1.png";
import AiVisual2 from "../img/screenshots/ai/AI2.png";
import AiVisual3 from "../img/screenshots/ai/AI3.png";
import AiVisual4 from "../img/screenshots/ai/AI4.png";
import { getAiTravelPlannerTranslations } from "../marketing/i18n/pages/aiTravelPlanner";
import { t } from "i18n/index";
import { localizeHref, MarketingLangCode } from "i18n/urlLocale";

const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

interface AiTravelPlannerPageProps {
	lang?: string;
}

const AiTravelPlannerPage = ({ lang }: AiTravelPlannerPageProps) => {
	const { match } = useReactRouter();
	const translations = getAiTravelPlannerTranslations(lang || "en");
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	// 123yb691qz1: every /register anchor on this page opens the signup modal in place instead
	// of navigating; `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store
	// instead. `match.path` reuses the label `handleTrack("signup")` already tracked under.
	const gate = usePlanGate(match.path, "store");

	const handleTrack = (type: "signup" | "download" | "appstore" | "googleplay") => {
		if (type === "appstore") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else if (type === "googleplay") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.Android && type === "download") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.IOS && type === "download") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else {
			trackEvent("click_signup_cta", { path: match.path }, "marketing_site");
			trackFathom("E4NT4HWN");
		}
	};

	// 123yb6937h3: both "Download the app" CTAs below resolve their href through this -- always a
	// real, attributed OneLink, including on the very first render before mount (see
	// useAppDownloadUrl's doc comment in useAppStoreLink.ts). Previously these were <button
	// onClick={setShowDownloadModal(true)}> with no href at all, so the server HTML had a dead
	// control -- confirmed on production by QA's curl retest of 123yb6937h3.
	const appDownloadUrl = useAppDownloadUrl();

	const [betaName, setBetaName] = useState("");
	const [betaEmail, setBetaEmail] = useState("");
	const [betaSubmitted, setBetaSubmitted] = useState(false);

	const handleBetaSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!betaEmail) return;
		trackEvent("ai_chat_beta_signup", { email: betaEmail, name: betaName }, "marketing_site");
		try {
			await axios.post("/api/utils/subscribers/aichatbeta", { email: betaEmail, name: betaName });
		} catch {
			// silent — analytics already fired, UX shows success regardless
		}
		setBetaSubmitted(true);
	};

	return (
		<div className="ai-planner-page" id="stippl-ai-planner">
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

			{/* HERO */}
			<section className="hr-hero">
				<svg className="hero-s-mark" width="500" height="666" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="white" />
				</svg>
				<svg className="hero-s-mark-2" width="300" height="400" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="white" />
				</svg>
				<div className="container__inner hr-hero__inner">
					<div className="hr-hero__content">
						<div className="hero-chip">{t(translations, "aiTravelPlanner.hero.chip", "AI Travel Planner")}</div>
						<h1>
							{t(translations, "aiTravelPlanner.hero.titlePrefix", "The ")}
							<span className="hr-hero__accent">{t(translations, "aiTravelPlanner.hero.titleAccent", "AI travel planner")}</span>
							{t(translations, "aiTravelPlanner.hero.titleSuffix", " you actually use on the trip")}
						</h1>
						<p className="hr-hero__desc">
							{t(translations, "aiTravelPlanner.hero.descPre", "Most AI travel tools give you a text response. ")}
							<strong>Stippl</strong>
							{t(
								translations,
								"aiTravelPlanner.hero.descPost",
								" gives you a structured, editable, shareable day-by-day itinerary: connected to your budget, your packing list, and your group. Not just a plan. A trip."
							)}
						</p>
						<div className="hero-ctas">
							{/* 123yb6937h3: on a phone the anchor is simply followed -- OneLink does the OS split
								(App Store vs Play Store) server-side, which is the ticket's acceptance criterion.
								On desktop we still want DownloadAppModal's QR code as the affordance, so the click
								handler -- browser-only, unlike a render-time device check -- prevents the default
								navigation and opens the modal instead. */}
							<a
								href={appDownloadUrl}
								className="hero-glass-btn"
								onClick={(e) => {
									handleTrack("download");
									if (!isMobile) {
										e.preventDefault();
										setShowDownloadModal(true);
									}
								}}
							>
								<div className="hero-glass-btn__icon">
									<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
										<path d={S_PATH} fill="#fff" />
									</svg>
								</div>
								<span className="hero-glass-btn__label">{t(translations, "aiTravelPlanner.cta.downloadApp", "Download the app")}</span>
							</a>
							<a
							href="/register"
							className="hero-web-link"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
								{t(translations, "aiTravelPlanner.cta.startOnWeb", "Start on web →")}
							</a>
						</div>
					</div>
					<img
						className="hr-hero__phone hr-hero__phone--front"
						src={AiVisual1}
						alt={t(translations, "aiTravelPlanner.hero.alt", "AI travel planner screenshot")}
						loading="eager"
					/>
				</div>
			</section>

			{/* STATS BAR */}
			{/* <section className="stats-bar">
				<div className="section-wrapper">
					<div className="stats-grid">
						<div className="stat-item">
							<div className="stat-num">1.5M+</div>
							<div className="stat-label">travellers worldwide (2026)</div>
						</div>
						<div className="stat-item">
							<div className="stat-num">&lt;2 min</div>
							<div className="stat-label">average AI itinerary generation time</div>
						</div>
						<div className="stat-item">
							<div className="stat-num">190+</div>
							<div className="stat-label">countries with AI itinerary support</div>
						</div>
						<div className="stat-item">
							<div className="stat-num">4.9★</div>
							<div className="stat-label">App Store rating · 12,400+ reviews</div>
						</div>
					</div>
				</div>
			</section> */}

			{/* HOW IT WORKS */}
			<HowItWorksSection
				theme="green"
				title={t(translations, "aiTravelPlanner.howToUse.name", AI_TRAVEL_PLANNER_CONTENT.howToUse.name)}
				description={t(translations, "aiTravelPlanner.howToUse.description", AI_TRAVEL_PLANNER_CONTENT.howToUse.description)}
				steps={AI_TRAVEL_PLANNER_CONTENT.howToSteps.map((step, index) => ({
					...step,
					name: t(translations, `aiTravelPlanner.howToSteps.${index}.name`, step.name),
					text: t(translations, `aiTravelPlanner.howToSteps.${index}.text`, step.text),
				}))}
				ctaText={t(translations, "aiTravelPlanner.howItWorks.cta", "Try the AI planner for free →")}
				onCtaClick={() => handleTrack("signup")}
			/>

			{/* COMPARISON SECTION */}
			<section className="bg-ice">
				<div className="section-wrapper">
					<div className="section-head center comparison-head">
						<h2>
							{t(translations, "aiTravelPlanner.comparison.titleLine1", "A travel plan in a chat window")}
							<br />
							{t(translations, "aiTravelPlanner.comparison.titleLine2", "isn't a travel plan.")}
						</h2>
						<p className="body-text">
							{t(
								translations,
								"aiTravelPlanner.comparison.description",
								"A conversation disappears. Stippl gives you something you can actually use on the trip."
							)}
						</p>
					</div>
					<div className="comparison-grid">
						<div className="comparison-card comparison-bad">
							<div className="comparison-label">{t(translations, "aiTravelPlanner.comparison.badLabel", "Planning in a chat")}</div>
							<div className="comparison-list">
								<div className="comparison-item">
									<span className="x-mark">✕</span>
									{t(translations, "aiTravelPlanner.comparison.badItem1", "Buried in a thread. Scroll to find it.")}
								</div>
								<div className="comparison-item">
									<span className="x-mark">✕</span>
									{t(translations, "aiTravelPlanner.comparison.badItem2", "Which version of Day 3 is the final one?")}
								</div>
								<div className="comparison-item">
									<span className="x-mark">✕</span>
									{t(translations, "aiTravelPlanner.comparison.badItem3", "Your partner has a different chat.")}
								</div>
								<div className="comparison-item">
									<span className="x-mark">✕</span>
									{t(translations, "aiTravelPlanner.comparison.badItem4", "No budget. No packing list. No map.")}
								</div>
								<div className="comparison-item">
									<span className="x-mark">✕</span>
									{t(translations, "aiTravelPlanner.comparison.badItem5", "Close the tab. Lose everything.")}
								</div>
							</div>
						</div>
						<div className="comparison-card comparison-good">
							<div className="comparison-label good-label">{t(translations, "aiTravelPlanner.comparison.goodLabel", "Stippl AI travel planner")}</div>
							<div className="comparison-list">
								<div className="comparison-item good-item">
									<span className="check-mark">✓</span>
									{t(translations, "aiTravelPlanner.comparison.goodItem1", "Structured itinerary. Editable. Always yours.")}
								</div>
								<div className="comparison-item good-item">
									<span className="check-mark">✓</span>
									{t(translations, "aiTravelPlanner.comparison.goodItem2", "One version. Everyone on the same page.")}
								</div>
								<div className="comparison-item good-item">
									<span className="check-mark">✓</span>
									{t(translations, "aiTravelPlanner.comparison.goodItem3", "Shared live with your whole group.")}
								</div>
								<div className="comparison-item good-item">
									<span className="check-mark">✓</span>
									{t(translations, "aiTravelPlanner.comparison.goodItem4", "Budget, packing list and map, all connected.")}
								</div>
								<div className="comparison-item good-item">
									<span className="check-mark">✓</span>
									{t(translations, "aiTravelPlanner.comparison.goodItem5", "Works offline. The plan lives in your pocket.")}
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE 1 — AI Itinerary Generator */}
			<section className="bg-white feature-section">
				<div className="section-wrapper">
					<div className="feature-split">
						<div className="feature-visual">
							<img src={AiVisual2} alt={t(translations, "aiTravelPlanner.feature1.alt", "AI itinerary generator")} />
						</div>
						<div className="feature-copy">
							<div className="section-chip chip-green">{t(translations, "aiTravelPlanner.feature1.chip", "AI Itinerary Generator")}</div>
							<h2>
								{t(translations, "aiTravelPlanner.feature1.titlePrefix", "A full itinerary personalised to you, ")}
								<span>{t(translations, "aiTravelPlanner.feature1.titleAccent", "in 2 minutes")}</span>
							</h2>
							<p>
								{t(
									translations,
									"aiTravelPlanner.feature1.description",
									"Every trip is different. Stippl's AI travel planner adapts to your travel style and interests, not a one-size-fits-all template. Want a slow food trip through Italy? A packed adventure across Southeast Asia? It builds around how you actually travel."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="plane" size={18} fill="var(--color-primary)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "aiTravelPlanner.feature1.item1.title", "Day-by-day plan for every destination")}</h4>
										<p>
											{t(
												translations,
												"aiTravelPlanner.feature1.item1.text",
												"Activities, restaurants, transport and accommodation for each stop on your route."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="marker-tick" size={18} fill="var(--color-primary)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "aiTravelPlanner.feature1.item2.title", "Adapts to your travel style")}</h4>
										<p>
											{t(
												translations,
												"aiTravelPlanner.feature1.item2.text",
												"Slow travel, packed schedule, adventure or culture, the AI builds around how you actually travel."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="globe" size={18} fill="var(--color-primary)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "aiTravelPlanner.feature1.item3.title", "190+ countries, all trip types")}</h4>
										<p>
											{t(
												translations,
												"aiTravelPlanner.feature1.item3.text",
												"Solo, couples, groups and families. City breaks, road trips, backpacking and beach holidays."
											)}
										</p>
									</div>
								</div>
							</div>
							<a href={localizeHref("/pro", lang as MarketingLangCode)} className="feature-cta-link">
								{t(translations, "aiTravelPlanner.feature1.cta", "Get Stippl Pro →")}
							</a>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE 2 — Day-by-Day Planning */}
			<section className="bg-ice feature-section">
				<div className="section-wrapper">
					<div className="feature-split reverse">
						<div className="feature-copy">
							<div className="section-chip chip-green">{t(translations, "aiTravelPlanner.feature2.chip", "Day-by-Day Planning")}</div>
							<h2>
								{t(translations, "aiTravelPlanner.feature2.titlePrefix", "Amazing ideas for ")}
								<span>{t(translations, "aiTravelPlanner.feature2.titleAccent", "how to fill every day")}</span>
							</h2>
							<p>
								{t(
									translations,
									"aiTravelPlanner.feature2.description",
									"Tell the AI your destination, travel style and what you're into, it builds each day around you. Not a generic list. A structured plan of things worth doing, tailored to how you actually travel."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="globe" size={18} fill="var(--color-primary)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "aiTravelPlanner.feature2.item1.title", "Structured day by day")}</h4>
										<p>
											{t(
												translations,
												"aiTravelPlanner.feature2.item1.text",
												"Each day has a clear shape: morning, afternoon and evening. You always know what's next."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="star--outlined" size={18} fill="var(--color-primary)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "aiTravelPlanner.feature2.item2.title", "Hidden gems alongside the highlights")}</h4>
										<p>
											{t(
												translations,
												"aiTravelPlanner.feature2.item2.text",
												"Not just the obvious tourist spots, the AI surfaces things that match your travel style."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="edit-pencil" size={18} stroke="var(--color-primary)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "aiTravelPlanner.feature2.item3.title", "Edit any day in one tap")}</h4>
										<p>
											{t(
												translations,
												"aiTravelPlanner.feature2.item3.text",
												"Swap anything out, add your own must-dos, rearrange the whole thing, it's fully yours."
											)}
										</p>
									</div>
								</div>
							</div>
							<a href={localizeHref("/itinerary-planner", lang as MarketingLangCode)} className="feature-cta-link">
								{t(translations, "aiTravelPlanner.feature2.cta", "See the full trip planner →")}
							</a>
						</div>
						<div className="feature-visual">
							<img src={AiVisual3} alt={t(translations, "aiTravelPlanner.feature2.alt", "Day-by-day itinerary planning")} />
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE 3 — Full Customisation */}
			<section className="bg-white feature-section">
				<div className="section-wrapper">
					<div className="feature-split">
						<div className="feature-visual">
							<img src={AiVisual4} alt={t(translations, "aiTravelPlanner.feature3.alt", "Customise your AI travel plan")} />
						</div>
						<div className="feature-copy">
							<div className="section-chip chip-green">{t(translations, "aiTravelPlanner.feature3.chip", "Full Customisation")}</div>
							<h2>
								{t(translations, "aiTravelPlanner.feature3.titlePrefix", "Add your own twist to ")}
								<span>{t(translations, "aiTravelPlanner.feature3.titleAccent", "any AI travel plan")}</span>
							</h2>
							<p>
								{t(
									translations,
									"aiTravelPlanner.feature3.description",
									"The AI creates the structure, you make it yours. Every suggestion is editable. Swap a restaurant for the one your friend recommended. Add a concert you already have tickets for. Remove anything that doesn't fit."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="star--outlined" size={18} fill="var(--color-primary)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "aiTravelPlanner.feature3.item1.title", "Add your own favourites")}</h4>
										<p>
											{t(
												translations,
												"aiTravelPlanner.feature3.item1.text",
												"Mix AI suggestions with your own must-dos, bookings and recommendations from friends."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="peoples" size={18} pathProps={{ stroke: "var(--color-primary)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "aiTravelPlanner.feature3.item2.title", "Share a live itinerary link")}</h4>
										<p>
											{t(
												translations,
												"aiTravelPlanner.feature3.item2.text",
												"Collaborators can view, suggest and edit, great for group trips where everyone has a say."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="phone" size={18} fill="var(--color-primary)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "aiTravelPlanner.feature3.item3.title", "Any device, always up to date")}</h4>
										<p>
											{t(translations, "aiTravelPlanner.feature3.item3.text", "Access and edit your plan on iOS, Android or web, everything syncs instantly.")}
										</p>
									</div>
								</div>
							</div>
							<a
							href="/register"
							className="feature-cta-link "
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
								{t(translations, "aiTravelPlanner.feature3.cta", "Start customising for free →")}
							</a>
						</div>
					</div>
				</div>
			</section>

			{/* PRO CALLOUT */}
			<section className="pro-callout-section">
				<div className="section-wrapper">
					<div className="pro-callout-block">
						<div className="pro-callout-body">
							<div className="pro-callout-label">{t(translations, "aiTravelPlanner.proCallout.label", "AI Superpowers")}</div>
							<h3>{t(translations, "aiTravelPlanner.proCallout.title", "Unlock Stippl's AI travel superpowers")}</h3>
							<p>
								{t(
									translations,
									"aiTravelPlanner.proCallout.description",
									"Generate a full route and day-by-day plan in under 2 minutes. Upgrade to get unlimited AI generations and early access to every new AI feature we ship."
								)}
							</p>
							<div className="pro-features-list">
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<Icon name="checkMark" size={12} fill="white" />
									</div>
									<span>{t(translations, "aiTravelPlanner.proCallout.feature1", "AI route generation, build your full trip route")}</span>
								</div>
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<Icon name="checkMark" size={12} fill="white" />
									</div>
									<span>{t(translations, "aiTravelPlanner.proCallout.feature2", "AI day plan generation: ideas for every day, tailored to you")}</span>
								</div>
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<Icon name="checkMark" size={12} fill="white" />
									</div>
									<span>{t(translations, "aiTravelPlanner.proCallout.feature3", "AI route optimisation, the smartest order to see everything")}</span>
								</div>
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<Icon name="checkMark" size={12} fill="white" />
									</div>
									<span>{t(translations, "aiTravelPlanner.proCallout.feature4", "Unlimited generations, plan as many trips as you want")}</span>
								</div>
								<div className="pro-feature-row coming-soon-row">
									<div className="pro-feature-check coming-soon-check">→</div>
									<span>{t(translations, "aiTravelPlanner.proCallout.comingSoonFeature", "AI Chat companion, coming soon")}</span>
								</div>
							</div>
						</div>
						<div className="pro-price-card">
							<div className="pro-price-label">{t(translations, "aiTravelPlanner.proCallout.priceLabel", "From")}</div>
							<div className="pro-price-num">€4.99</div>
							<div className="pro-price-period">{t(translations, "aiTravelPlanner.proCallout.pricePeriod", "per month")}</div>
							<a href={localizeHref("/pro", lang as MarketingLangCode)} className="pro-price-btn">
								<svg width="14" height="18" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="currentColor" />
								</svg>
								{t(translations, "aiTravelPlanner.proCallout.priceBtn", "Get Pro →")}
							</a>
							<div className="pro-price-note">{t(translations, "aiTravelPlanner.proCallout.priceNote", "No credit card required")}</div>
						</div>
					</div>
				</div>
			</section>

			{/* COMING SOON — AI Chat */}
			<section className="coming-soon-section">
				<svg className="cs-s-mark" width="500" height="666" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="white" />
				</svg>
				<div className="section-wrapper cs-inner">
					<div className="cs-grid">
						<div className="cs-copy">
							<div className="cs-badge">
								<div className="cs-pulse"></div>
								{t(translations, "aiTravelPlanner.comingSoon.badge", "Coming Soon")}
							</div>
							<h2>
								{t(translations, "aiTravelPlanner.comingSoon.titlePrefix", "Meet your AI travel ")}
								<span>{t(translations, "aiTravelPlanner.comingSoon.titleAccent", "chat companion")}</span>
							</h2>
							<p>
								{t(
									translations,
									"aiTravelPlanner.comingSoon.description",
									"Imagine having a travel expert in your pocket: one that already knows your itinerary, your budget, and your travel style. Ask it anything. Change anything. Get recommendations on the fly."
								)}
							</p>
							<div className="cs-perks">
								<div className="cs-perk">
									<div className="cs-perk-icon">
										<Icon name="chat" size={18} fill="#fff" />
									</div>
									{t(translations, "aiTravelPlanner.comingSoon.perk1", 'Chat naturally about your trip, "what should I do on Day 3 if it rains?"')}
								</div>
								<div className="cs-perk">
									<div className="cs-perk-icon">
										<Icon name="edit" size={18} fill="#fff" />
									</div>
									{t(
										translations,
										"aiTravelPlanner.comingSoon.perk2",
										'Edit your itinerary through conversation, "swap dinner on Day 2 to a vegetarian place"'
									)}
								</div>
								<div className="cs-perk">
									<div className="cs-perk-icon">
										<Icon name="marker" size={18} fill="#fff" />
									</div>
									{t(translations, "aiTravelPlanner.comingSoon.perk3", "Get real-time recommendations while you're on the road")}
								</div>
								<div className="cs-perk">
									<div className="cs-perk-icon">
										<Icon name="star" size={18} fill="#fff" />
									</div>
									{t(translations, "aiTravelPlanner.comingSoon.perk4", "Pro subscribers get early access first")}
								</div>
							</div>
						</div>
						<div className="signup-card">
							{betaSubmitted ? (
								<div className="signup-success">
									<div className="signup-success-icon">✓</div>
									<h3>{t(translations, "aiTravelPlanner.signup.successTitle", "You're on the list!")}</h3>
									<p>
										{t(
											translations,
											"aiTravelPlanner.signup.successDescription",
											"We'll email you as soon as AI Chat is ready. Pro subscribers get first access."
										)}
									</p>
								</div>
							) : (
								<>
									<h3>{t(translations, "aiTravelPlanner.signup.title", "Get early access to AI Chat")}</h3>
									<p>
										{t(
											translations,
											"aiTravelPlanner.signup.description",
											"Be the first to try Stippl's AI chat when it launches. Early access goes to our beta list first, sign up to join."
										)}
									</p>
									<div className="chat-preview">
										<div className="chat-preview-label">{t(translations, "aiTravelPlanner.signup.previewLabel", "Preview")}</div>
										<div className="chat-msg user">{t(translations, "aiTravelPlanner.signup.chatUser", "What should I do in Tokyo if it rains on Day 2?")}</div>
										<div className="chat-msg ai">
											{t(
												translations,
												"aiTravelPlanner.signup.chatAi",
												"Great question! Since you have teamLab Borderless on your list, rain is the perfect excuse to move it to Day 2. I'd also suggest…"
											)}
										</div>
										<div className="chat-typing">
											<span></span>
											<span></span>
											<span></span>
										</div>
									</div>
									<form className="signup-form" onSubmit={handleBetaSubmit}>
										<input
											type="text"
											className="signup-input"
											placeholder={t(translations, "aiTravelPlanner.signup.namePlaceholder", "Your name")}
											value={betaName}
											onChange={(e) => setBetaName(e.target.value)}
										/>
										<input
											type="email"
											className="signup-input"
											placeholder={t(translations, "aiTravelPlanner.signup.emailPlaceholder", "Your email address")}
											value={betaEmail}
											onChange={(e) => setBetaEmail(e.target.value)}
											required
										/>
										<button type="submit" className="signup-btn">
											{t(translations, "aiTravelPlanner.signup.submitBtn", "Join the AI Chat beta →")}
										</button>
									</form>
									<div className="signup-privacy">
										{t(translations, "aiTravelPlanner.signup.privacyNote", "No spam, ever. Unsubscribe any time. Beta invites sent in order of sign-up.")}
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section className="feature-section">
				<FAQSection
					heading={t(translations, "aiTravelPlanner.faq.heading", "Frequently asked questions about Stippl's AI travel planner")}
					items={AI_TRAVEL_PLANNER_CONTENT.faqItems.map((item, index) => ({
						...item,
						question: t(translations, `aiTravelPlanner.faqItems.${index}.question`, item.question),
						answer: t(translations, `aiTravelPlanner.faqItems.${index}.answer`, item.answer),
					}))}
				/>
			</section>

			{/* CTA STRIP */}
			<section className="cta-strip">
				<svg className="cta-s-mark" width="400" height="533" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="white" />
				</svg>
				<div className="cta-strip-inner">
					<h2>{t(translations, "aiTravelPlanner.ctaStrip.title", "Plan your next trip with AI")}</h2>
					<p>{t(translations, "aiTravelPlanner.ctaStrip.description", "Your first AI itinerary is free. Build, share and live your trip, all in one app.")}</p>
					<div className="cta-strip-btns">
						{/* Same fix as the hero CTA above (123yb6937h3): a real href on the server render, store
							link on mobile, modal-with-QR on desktop. */}
						<a
							href={appDownloadUrl}
							className="btn btn-white"
							onClick={(e) => {
								handleTrack("download");
								if (!isMobile) {
									e.preventDefault();
									setShowDownloadModal(true);
								}
							}}
						>
							{t(translations, "aiTravelPlanner.cta.downloadApp", "Download the app")}
						</a>
						<a
						href="/register"
						className="btn btn-outline-white"
						onClick={(e) => {
							e.preventDefault();
							gate.requestPlan();
						}}
					>
							{t(translations, "aiTravelPlanner.cta.startOnWeb", "Start on web →")}
						</a>
					</div>
				</div>
			</section>

			<Footer />
			<DownloadAppQrCard />
			<DownloadAppModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} location={match.path} />
			<PlanGateModal gate={gate} title="Create a free account to start planning" />
		</div>
	);
};

export default AiTravelPlannerPage;
